// The in-memory API's teams and org chart (M1-08 fake twin of M1-04's routes): teams CRUD and
// members, `GET org-chart`, `PUT org-chart/edges` and `POST org-chart/simulate`. The edge rules
// are the API's own (`@companyos/domain-work/org` `planEdges`, and the team containment rules),
// so a draft the fake accepts is one the API accepts. The seats and their holders are the
// structure fake's (one store: a seat re-parented here is re-parented in Setup › Seats).
// Shapes are the SDK's; `fake-teams-contract.test.ts` parses every response. (Responsibilities,
// recurring work and templates are fake-org.ts.)
import {
  type ChartSeat,
  compositionOf,
  descendantsOf,
  type EdgeKind,
  type EdgePolicy,
  hierarchyError,
  normalPolicy,
  planEdges,
  type StoredEdge,
} from "@companyos/domain-work/org";
import type { OperationOutput, Problem, RouteId } from "@companyos/sdk";
import type { Handler, Principal } from "./fake";
import { COMPANY_IDS, type FakeCompany, SHARED_DE, uid } from "./fake-data";
import { SHARED_DES_TEAMS } from "./fake-shared-des";
import type { Seat, SeatAssignment } from "./types";

type Team = OperationOutput<"teams.get">;
type TeamRow = Omit<Team, "members">;
type TeamMember = Team["members"][number];
type SeatEdge = OperationOutput<"orgChart.get">["edges"][number];

export type TeamsDeps = {
  json: (body: unknown, status?: number) => Response;
  problem: (status: number, code: Problem["code"], title: string, detail?: string) => Response;
  notFound: () => Response;
  validation: (detail: string) => Response;
  page: <T>(items: T[], url: URL) => unknown;
  now: () => Date;
  nextSeq: () => number;
  /** The structure fake's seats and assignments of the company (mutable, shared). */
  seatsOf: (company: FakeCompany) => Seat[];
  assignmentsOf: (company: FakeCompany) => SeatAssignment[];
  /** A membership's display name (person or DE). */
  nameOf: (company: FakeCompany, membershipId: string) => string;
  /** WORKFORCE: a team was deleted (its agents lose the team, as production). */
  onDeleted?: (p: Principal, teamId: string) => void;
  /** CHART-AGENTS: the company's live agents (AG; fake-workforce.ts), not yet cut to the chart. */
  agentsOf?: (p: Principal) => OperationOutput<"orgChart.get">["agents"];
  /** SHARED-DES: the company's serves links (the chart's `service_links`). */
  serviceLinksOf?: (company: FakeCompany) => OperationOutput<"orgChart.get">["service_links"];
  /** SHELL-DECISIONS: the DEs a member is shown (null: every DE). */
  desShownTo?: (p: Principal) => Set<string> | null;
  /** Hands the team and member stores to the finance twin (M5-04: its tree reads them). */
  shareTeams?: (stores: {
    teamsOf: (company: FakeCompany) => TeamRow[];
    membersOf: (company: FakeCompany) => TeamMember[];
  }) => void;
};

const EPOCH = "2026-06-01T09:00:00Z";
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const KINDS: readonly EdgeKind[] = ["reports_to", "authority", "delegation"];

export function teamsHandlers(d: TeamsDeps): Partial<Record<RouteId, Handler>> {
  const { json, problem, notFound, validation } = d;
  const forbidden = () => problem(403, "forbidden", "Forbidden");
  const conflict = (detail: string) => problem(409, "conflict", "Conflict", detail);
  const stamp = () => d.now().toISOString();
  const actor = (p: Principal) => ({
    actor_kind: p.operator ? ("agency_user" as const) : ("person" as const),
    actor_membership_id: null,
  });

  // Per-company state, seeded on first use -----------------------------------------------------

  const teamStore = new Map<string, TeamRow[]>();
  const memberStore = new Map<string, TeamMember[]>();
  const edgeStore = new Map<string, SeatEdge[]>();
  /** CHART-TABLE-ORDER: each seat's place among its supervisor's seats (`seat.sort_order`). */
  const orderStore = new Map<string, Map<string, number>>();
  const orderOf = (company: FakeCompany) => {
    const m = orderStore.get(company.id) ?? new Map<string, number>();
    orderStore.set(company.id, m);
    return m;
  };

  const active = (company: FakeCompany, seatId: string) =>
    d.assignmentsOf(company).filter((a) => a.seat_id === seatId && a.active_to === null);

  /** Seeds: the supervisor's team (mixed) and its DE seats' sub-team (digital, human-owned). */
  function seed(company: FakeCompany) {
    const seats = d.seatsOf(company);
    const de = d.assignmentsOf(company).filter((a) => a.membership_kind === "digital_employee");
    const allDeSeats = [...new Set(de.map((a) => a.seat_id))];
    const lead = seats.find((s) => allDeSeats.includes(s.id))?.supervisor_seat_id ?? null;
    // The DE seats under that supervisor (SHARED-DES: Acme's Account Health has its own team).
    const deSeats = allDeSeats.filter(
      (id) => seats.find((s) => s.id === id)?.supervisor_seat_id === lead,
    );
    const teams: TeamRow[] = [];
    const members: TeamMember[] = [];
    const edges: SeatEdge[] = [];
    const n = Number.parseInt(company.id.slice(-4), 16) % 0x0fff;
    const row = (i: number, name: string, parent: string | null, extra: Partial<TeamRow>) => ({
      company_id: company.id,
      id: uid(0x3a00 + n, i),
      name,
      parent_team_id: parent,
      lead_seat_id: null,
      composition: "human" as const,
      human_owner_seat_id: null,
      actor_kind: "system" as const,
      actor_membership_id: null,
      created_at: EPOCH,
      updated_at: EPOCH,
      ...extra,
    });
    const member = (team: string, seat: string): TeamMember => ({
      company_id: company.id,
      team_id: team,
      seat_id: seat,
      actor_kind: "system",
      actor_membership_id: null,
      created_at: EPOCH,
    });
    if (lead && deSeats.length > 0) {
      const top = row(1, `${seats.find((s) => s.id === lead)?.title ?? "Lead"}'s team`, null, {
        lead_seat_id: lead,
      });
      const desk = row(2, "Digital desk", top.id, {
        lead_seat_id: deSeats[0] ?? null,
        human_owner_seat_id: lead,
      });
      teams.push(top, desk);
      for (const s of [lead, ...deSeats]) members.push(member(top.id, s));
      for (const s of deSeats) members.push(member(desk.id, s));
      const edge = (i: number, to: string, kind: EdgeKind, policy: EdgePolicy): SeatEdge => ({
        company_id: company.id,
        id: uid(0x3b00 + n, i),
        from_seat_id: lead,
        to_seat_id: to,
        kind,
        policy,
        actor_kind: "system",
        actor_membership_id: null,
        created_at: EPOCH,
        updated_at: EPOCH,
      });
      edges.push(edge(1, deSeats[0] as string, "authority", {}));
      if (deSeats[1]) edges.push(edge(2, deSeats[1], "delegation", { max_depth: 2 }));
    }
    // SHARED-DES: Acme's Fulfillment, Sales and Recruiting teams (fake-shared-des.ts).
    if (company.id === COMPANY_IDS.acme && seats.some((s) => s.id === SHARED_DE.deSeat))
      SHARED_DES_TEAMS.forEach((t, i) => {
        teams.push(row(3 + i, t.name, null, { id: t.id, lead_seat_id: t.lead }));
        for (const s of t.members) members.push(member(t.id, s));
      });
    teamStore.set(company.id, teams);
    memberStore.set(company.id, members);
    edgeStore.set(company.id, edges);
    for (const t of teams) t.composition = composition(company, t.id);
  }

  function teamsOf(company: FakeCompany): TeamRow[] {
    if (!teamStore.has(company.id)) seed(company);
    return teamStore.get(company.id) as TeamRow[];
  }
  function membersOf(company: FakeCompany): TeamMember[] {
    if (!memberStore.has(company.id)) seed(company);
    return memberStore.get(company.id) as TeamMember[];
  }
  d.shareTeams?.({ teamsOf, membersOf });
  function edgesOf(company: FakeCompany): SeatEdge[] {
    if (!edgeStore.has(company.id)) seed(company);
    return edgeStore.get(company.id) as SeatEdge[];
  }

  function composition(company: FakeCompany, teamId: string) {
    return compositionOf(
      (memberStore.get(company.id) ?? [])
        .filter((m) => m.team_id === teamId)
        .map((m) => {
          const holders = active(company, m.seat_id);
          return {
            person: holders.some((h) => h.membership_kind === "person"),
            digital: holders.some((h) => h.membership_kind === "digital_employee"),
          };
        }),
    );
  }

  const graph = (company: FakeCompany) =>
    teamsOf(company).map((t) => ({
      id: t.id,
      parentTeamId: t.parent_team_id,
      memberIds: membersOf(company)
        .filter((m) => m.team_id === t.id)
        .map((m) => m.seat_id),
    }));

  /** The caller's active seats (the fake's demo client viewers hold none). */
  const heldBy = (p: Principal) =>
    new Set(
      d
        .assignmentsOf(p.company)
        .filter(
          (a) =>
            a.active_to === null &&
            p.me.memberships.some((m) => m.membership_id === a.membership_id),
        )
        .map((a) => a.seat_id),
    );

  /** 05 `team#viewer`: managers see every team; others the teams their seats are in. */
  function visible(p: Principal): TeamRow[] {
    if (p.manager) return teamsOf(p.company);
    const held = heldBy(p);
    return teamsOf(p.company).filter(
      (t) =>
        (t.lead_seat_id && held.has(t.lead_seat_id)) ||
        (t.human_owner_seat_id && held.has(t.human_owner_seat_id)) ||
        membersOf(p.company).some((m) => m.team_id === t.id && held.has(m.seat_id)),
    );
  }

  /** A seat a team may name: in the company and not proposed (the API's 422 otherwise). */
  function teamSeat(p: Principal, id: unknown, path: string): Seat | Response {
    const s = typeof id === "string" ? d.seatsOf(p.company).find((x) => x.id === id) : undefined;
    if (!s) return validation(`${path}: no such seat in this company`);
    if (s.status === "proposed") return validation(`${path}: the seat is proposed`);
    return s;
  }

  function checkSeats(p: Principal, b: Record<string, unknown>): Response | null {
    for (const field of ["lead_seat_id", "human_owner_seat_id"] as const) {
      if (b[field] === undefined || b[field] === null) continue;
      const s = teamSeat(p, b[field], field);
      if (s instanceof Response) return s;
      if (s.status === "closed") return problem(409, "seat_closed", "Seat is closed");
    }
    return null;
  }

  const ownerMissing = () =>
    validation(
      "human_owner_seat_id: a team of digital employees needs a human owner seat; set it first",
    );

  /** Re-derives a team's composition after a member change (refused if it needs an owner). */
  function refresh(company: FakeCompany, t: TeamRow): Response | null {
    const next = composition(company, t.id);
    if (next === t.composition) return null;
    if (next === "digital" && t.human_owner_seat_id === null) return ownerMissing();
    t.composition = next;
    t.updated_at = stamp();
    return null;
  }

  // The org chart -------------------------------------------------------------------------------

  function chartSeats(p: Principal): ChartSeat[] {
    return d.seatsOf(p.company).map((s) => {
      const holders = active(p.company, s.id);
      return {
        id: s.id,
        status: s.status,
        supervisorSeatId: s.supervisor_seat_id,
        person: holders.some((h) => h.membership_kind === "person"),
        digital: holders.some((h) => h.membership_kind === "digital_employee"),
      };
    });
  }

  function stored(p: Principal): StoredEdge[] {
    return edgesOf(p.company).map((e) => ({
      id: e.id,
      from: e.from_seat_id,
      to: e.to_seat_id,
      kind: e.kind,
      policy: e.policy,
    }));
  }

  function chart(p: Principal) {
    const seats = d.seatsOf(p.company).filter((s) => s.status !== "proposed");
    const ids = new Set(seats.map((s) => s.id));
    const sup = new Map(seats.map((s) => [s.id, s.supervisor_seat_id]));
    const teams = visible(p);
    const teamIds = new Set(teams.map((t) => t.id));
    const byId = <T extends { id: string }>(a: T, b: T) => a.id.localeCompare(b.id);
    // CHART-AGENTS: only agents under a holder the chart shows, grouped by that holder.
    const shown = new Set(
      seats.flatMap((s) => active(p.company, s.id).map((a) => a.membership_id)),
    );
    const agents = (d.agentsOf?.(p) ?? [])
      .filter((a) => shown.has(a.reports_to_membership_id))
      .sort(
        (a, b) =>
          a.reports_to_membership_id.localeCompare(b.reports_to_membership_id) ||
          a.name.localeCompare(b.name) ||
          a.id.localeCompare(b.id),
      );
    return {
      agents,
      seats: [...seats].sort(byId).map((s) => ({
        id: s.id,
        role_id: s.role_id,
        title: s.title,
        status: s.status,
        supervisor_seat_id: s.supervisor_seat_id,
        staff: Boolean(s.staff) && s.supervisor_seat_id !== null,
        sort_order: orderOf(p.company).get(s.id) ?? null,
        holders: active(p.company, s.id)
          .sort(
            (a, b) =>
              b.membership_kind.localeCompare(a.membership_kind) ||
              a.membership_id.localeCompare(b.membership_id),
          )
          .map((a) => ({
            membership_id: a.membership_id,
            membership_kind: a.membership_kind,
            display_name: d.nameOf(p.company, a.membership_id),
            share: a.share,
          })),
      })),
      edges: edgesOf(p.company)
        .filter(
          (e) =>
            ids.has(e.from_seat_id) &&
            ids.has(e.to_seat_id) &&
            (e.kind !== "reports_to" || sup.get(e.from_seat_id) === e.to_seat_id),
        )
        .sort(byId),
      teams: [...teams].sort(byId),
      team_members: membersOf(p.company)
        .filter((m) => teamIds.has(m.team_id))
        .sort((a, b) => a.team_id.localeCompare(b.team_id) || a.seat_id.localeCompare(b.seat_id)),
      // SHARED-DES: every chart viewer reads the links (they draw the linked cards).
      // SHARED-DES-2: a link to a team the viewer may not see is not on its chart.
      ...(d.serviceLinksOf
        ? {
            service_links: (d.serviceLinksOf(p.company) ?? []).filter(
              (l) =>
                ids.has(l.seat_id) &&
                (l.target_kind !== "seat" || ids.has(l.target_id ?? "")) &&
                (l.target_kind !== "team" || teamIds.has(l.target_id ?? "")),
            ),
          }
        : {}),
    };
  }

  /** The body as the plan's input, or a 422 for a malformed one (the contract's zod rules). */
  function readEdges(body: unknown) {
    const b = (body ?? {}) as { kinds?: unknown; edges?: unknown };
    if (b.kinds !== undefined) {
      if (
        !Array.isArray(b.kinds) ||
        b.kinds.length < 1 ||
        b.kinds.length > 3 ||
        b.kinds.some((k) => !KINDS.includes(k))
      )
        return validation("kinds: one to three of reports_to, authority, delegation");
    }
    if (!Array.isArray(b.edges) || b.edges.length > 2000)
      return validation("edges: an array of at most 2000 edges");
    const edges = [];
    for (const [i, e] of (b.edges as Record<string, unknown>[]).entries()) {
      if (
        typeof e?.from_seat_id !== "string" ||
        !UUID.test(e.from_seat_id) ||
        typeof e.to_seat_id !== "string" ||
        !UUID.test(e.to_seat_id) ||
        !KINDS.includes(e.kind as EdgeKind)
      )
        return validation(`edges.${i}: from_seat_id, to_seat_id and kind are required`);
      const policy = e.policy as EdgePolicy | undefined;
      if (policy !== undefined && (typeof policy !== "object" || policy === null))
        return validation(`edges.${i}.policy: an object`);
      edges.push({
        from: e.from_seat_id,
        to: e.to_seat_id,
        kind: e.kind as EdgeKind,
        ...(policy ? { policy } : {}),
      });
    }
    return { ...(b.kinds ? { kinds: b.kinds as EdgeKind[] } : {}), edges };
  }

  function plan(p: Principal, body: unknown) {
    const input = readEdges(body);
    if (input instanceof Response) return input;
    const out = planEdges(chartSeats(p), stored(p), input);
    if (out.inputErrors.length > 0)
      return validation(out.inputErrors.map((e) => `${e.path}: ${e.message}`).join("; "));
    return out;
  }

  // Handlers ------------------------------------------------------------------------------------

  const teamOf = (p: Principal, id: string | undefined) =>
    teamsOf(p.company).find((t) => t.id === id) ?? null;

  return {
    "teams.list": ({ principal, url }) => {
      if (!principal) return notFound();
      const rows = [...visible(principal)].sort((a, b) => a.id.localeCompare(b.id));
      return json(d.page(rows, url));
    },
    "teams.get": ({ principal, params }) => {
      if (!principal) return notFound();
      const t = visible(principal).find((x) => x.id === params.id);
      if (!t) return notFound();
      return json({
        ...t,
        members: membersOf(principal.company)
          .filter((m) => m.team_id === t.id)
          .sort((a, b) => a.seat_id.localeCompare(b.seat_id)),
      });
    },
    "teams.create": ({ principal, body }) => {
      if (!principal) return notFound();
      if (!principal.manager) return forbidden();
      const b = (body ?? {}) as Record<string, unknown>;
      if (typeof b.name !== "string" || !b.name.trim()) return validation("name must not be empty");
      const parent = b.parent_team_id ?? null;
      if (parent !== null && !teamOf(principal, parent as string))
        return validation("parent_team_id: no such team in this company");
      const bad = checkSeats(principal, b);
      if (bad) return bad;
      const name = b.name.trim();
      if (teamsOf(principal.company).some((t) => t.name === name))
        return conflict("a team with this name exists in the company");
      const at = stamp();
      const row: TeamRow = {
        company_id: principal.company.id,
        id: uid(0x3c00, d.nextSeq()),
        name,
        parent_team_id: parent as string | null,
        lead_seat_id: (b.lead_seat_id as string | null | undefined) ?? null,
        composition: "human",
        human_owner_seat_id: (b.human_owner_seat_id as string | null | undefined) ?? null,
        ...actor(principal),
        created_at: at,
        updated_at: at,
      };
      teamsOf(principal.company).push(row);
      return json(row, 201);
    },
    "teams.update": ({ principal, params, body }) => {
      if (!principal) return notFound();
      if (!principal.manager) return forbidden();
      const t = teamOf(principal, params.id);
      if (!t) return notFound();
      const b = (body ?? {}) as Record<string, unknown>;
      if (b.name !== undefined && (typeof b.name !== "string" || !b.name.trim()))
        return validation("name must not be empty");
      if (b.parent_team_id !== undefined && b.parent_team_id !== t.parent_team_id) {
        const parent = (b.parent_team_id as string | null) ?? null;
        if (parent !== null && !teamOf(principal, parent))
          return validation("parent_team_id: no such team in this company");
        const g = graph(principal.company);
        const node = g.find((n) => n.id === t.id);
        const error = node && hierarchyError({ ...node, parentTeamId: parent }, g);
        if (error?.code === "cycle")
          return problem(422, "org_chart_cycle", "The edges would form a cycle", error.message);
        if (error) return conflict(error.message);
      }
      const changed = (k: "lead_seat_id" | "human_owner_seat_id") =>
        b[k] !== undefined && b[k] !== t[k];
      const bad = checkSeats(principal, {
        ...(changed("lead_seat_id") ? { lead_seat_id: b.lead_seat_id } : {}),
        ...(changed("human_owner_seat_id") ? { human_owner_seat_id: b.human_owner_seat_id } : {}),
      });
      if (bad) return bad;
      if (changed("human_owner_seat_id") && t.composition === "digital" && !b.human_owner_seat_id)
        return ownerMissing();
      const name = typeof b.name === "string" ? b.name.trim() : t.name;
      if (name !== t.name && teamsOf(principal.company).some((x) => x.name === name))
        return conflict("a team with this name exists in the company");
      const next = {
        name,
        parent_team_id:
          b.parent_team_id === undefined ? t.parent_team_id : (b.parent_team_id as string | null),
        lead_seat_id:
          b.lead_seat_id === undefined ? t.lead_seat_id : (b.lead_seat_id as string | null),
        human_owner_seat_id:
          b.human_owner_seat_id === undefined
            ? t.human_owner_seat_id
            : (b.human_owner_seat_id as string | null),
      };
      const same = (Object.keys(next) as (keyof typeof next)[]).every((k) => next[k] === t[k]);
      if (!same) Object.assign(t, next, actor(principal), { updated_at: stamp() });
      return json(t);
    },
    "teams.delete": ({ principal, params }) => {
      if (!principal) return notFound();
      if (!principal.manager) return forbidden();
      const t = teamOf(principal, params.id);
      if (!t) return notFound();
      if (teamsOf(principal.company).some((x) => x.parent_team_id === t.id))
        return conflict("move or delete the team's sub-teams first");
      teamStore.set(
        principal.company.id,
        teamsOf(principal.company).filter((x) => x.id !== t.id),
      );
      memberStore.set(
        principal.company.id,
        membersOf(principal.company).filter((m) => m.team_id !== t.id),
      );
      d.onDeleted?.(principal, t.id);
      return new Response(null, { status: 204 });
    },
    "teams.addMember": ({ principal, params, body }) => {
      if (!principal) return notFound();
      if (!principal.manager) return forbidden();
      const t = teamOf(principal, params.id);
      if (!t) return notFound();
      const s = teamSeat(principal, (body as { seat_id?: unknown } | null)?.seat_id, "seat_id");
      if (s instanceof Response) return s;
      if (s.status === "closed") return problem(409, "seat_closed", "Seat is closed");
      const g = graph(principal.company);
      const node = g.find((n) => n.id === t.id);
      if (!node) return notFound();
      if (node.memberIds.includes(s.id))
        return conflict("the seat is already a member of the team");
      const error = hierarchyError({ ...node, memberIds: [...node.memberIds, s.id] }, g);
      if (error) return conflict(error.message);
      const row: TeamMember = {
        company_id: principal.company.id,
        team_id: t.id,
        seat_id: s.id,
        ...actor(principal),
        created_at: stamp(),
      };
      membersOf(principal.company).push(row);
      const refused = refresh(principal.company, t);
      if (refused) {
        membersOf(principal.company).pop();
        return refused;
      }
      return json(row, 201);
    },
    "teams.removeMember": ({ principal, params }) => {
      if (!principal) return notFound();
      if (!principal.manager) return forbidden();
      const t = teamOf(principal, params.id);
      if (!t) return notFound();
      const g = graph(principal.company);
      if (!g.find((n) => n.id === t.id)?.memberIds.includes(params.seat_id ?? ""))
        return notFound();
      // Out of the team and every team under it that has the seat (containment).
      const from = new Set([t.id, ...descendantsOf(t.id, g)]);
      const before = membersOf(principal.company);
      memberStore.set(
        principal.company.id,
        before.filter((m) => !(from.has(m.team_id) && m.seat_id === params.seat_id)),
      );
      for (const x of teamsOf(principal.company).filter((x) => from.has(x.id))) {
        const refused = refresh(principal.company, x);
        if (refused) {
          memberStore.set(principal.company.id, before);
          return refused;
        }
      }
      return new Response(null, { status: 204 });
    },

    "orgChart.get": ({ principal }) => {
      if (!principal) return notFound();
      // 05 `org_chart#viewer`: managers and seat holders, and (MEMBER-ROLE) company#member.
      if (!principal.manager && !principal.roles.includes("member") && heldBy(principal).size === 0)
        return forbidden();
      const out = chart(principal);
      // SHELL-DECISIONS: a member's chart lists only the DEs they work with.
      const shown = d.desShownTo?.(principal) ?? null;
      if (shown) {
        // An AG reporting to a hidden DE is not listed; a hidden DE's use is not named.
        out.agents = out.agents
          .filter(
            (a) =>
              a.reports_to_kind !== "digital_employee" || shown.has(a.reports_to_membership_id),
          )
          .map((a) => ({
            ...a,
            ...(a.used_by_membership_ids
              ? { used_by_membership_ids: a.used_by_membership_ids.filter((id) => shown.has(id)) }
              : {}),
          }));
      }
      if (shown)
        out.seats = out.seats.map((s) => ({
          ...s,
          holders: s.holders.filter(
            (h) => h.membership_kind !== "digital_employee" || shown.has(h.membership_id),
          ),
        }));
      return json(out);
    },
    "orgChart.simulate": ({ principal, body }) => {
      if (!principal) return notFound();
      if (!principal.manager) return forbidden();
      const out = plan(principal, body);
      if (out instanceof Response) return out;
      return json({
        valid: out.problems.length === 0,
        problems: out.problems.map((x) => ({
          code: x.code,
          seat_ids: x.seatIds,
          message: x.message,
        })),
        added: out.added.map((e) => ({
          from_seat_id: e.from,
          to_seat_id: e.to,
          kind: e.kind,
          policy: normalPolicy(e.policy),
        })),
        updated: out.updated.map((u) => ({
          ...(edgesOf(principal.company).find((e) => e.id === u.edge.id) as SeatEdge),
          policy: normalPolicy(u.policy),
        })),
        removed: out.removed.map(
          (r) => edgesOf(principal.company).find((e) => e.id === r.id) as SeatEdge,
        ),
        supervisor_changes: out.supervisorChanges.map((c) => ({
          seat_id: c.seatId,
          from: c.from,
          to: c.to,
        })),
      });
    },
    "orgChart.putEdges": ({ principal, body }) => {
      if (!principal) return notFound();
      if (!principal.manager) return forbidden();
      const out = plan(principal, body);
      if (out instanceof Response) return out;
      if (out.problems.length > 0) {
        const codes = out.problems.map((x) => x.code);
        const detail = out.problems.map((x) => x.message).join("; ");
        if (codes.includes("cycle"))
          return problem(422, "org_chart_cycle", "The edges would form a cycle", detail);
        if (codes.includes("supervisor_required"))
          return problem(
            422,
            "supervisor_required",
            "Supervisor required: the reporting chain must end at a person",
            detail,
          );
        if (codes.includes("seat_closed"))
          return problem(409, "seat_closed", "Seat is closed", detail);
        return validation(detail);
      }
      const at = stamp();
      const removed = new Set(out.removed.map((e) => e.id));
      const edges = edgesOf(principal.company).filter((e) => !removed.has(e.id));
      for (const u of out.updated) {
        const e = edges.find((x) => x.id === u.edge.id);
        if (e) Object.assign(e, { policy: normalPolicy(u.policy), updated_at: at });
      }
      for (const a of out.added)
        edges.push({
          company_id: principal.company.id,
          id: uid(0x3d00, d.nextSeq()),
          from_seat_id: a.from,
          to_seat_id: a.to,
          kind: a.kind,
          policy: normalPolicy(a.policy),
          ...actor(principal),
          created_at: at,
          updated_at: at,
        });
      edgeStore.set(principal.company.id, edges);
      for (const c of out.supervisorChanges) {
        orderOf(principal.company).delete(c.seatId);
        const s = d.seatsOf(principal.company).find((x) => x.id === c.seatId);
        if (s)
          Object.assign(s, {
            supervisor_seat_id: c.to,
            ...(c.to === null ? { staff: false } : {}),
            version: s.version + 1,
            updated_at: at,
          });
      }
      return json(chart(principal));
    },
    // CHART-TABLE-ORDER: as production — managers only; every listed seat must already report to
    // the supervisor (no re-parenting), none twice; refusals name positions, never ids.
    "orgChart.putOrder": ({ principal, body }) => {
      if (!principal) return notFound();
      if (!principal.manager) return forbidden();
      const b = body as { supervisor_seat_id: string | null; seat_ids: string[] };
      if (new Set(b.seat_ids).size !== b.seat_ids.length)
        return validation("seat_ids: a seat is listed twice");
      const seats = d.seatsOf(principal.company);
      const bad = b.seat_ids.findIndex((id) => {
        const s = seats.find((x) => x.id === id);
        return !s || s.status === "proposed" || s.supervisor_seat_id !== b.supervisor_seat_id;
      });
      if (bad >= 0) return validation(`seat_ids.${bad}: does not report to that supervisor`);
      const order = orderOf(principal.company);
      b.seat_ids.forEach((id, i) => {
        if (order.get(id) === i) return;
        order.set(id, i);
        const s = seats.find((x) => x.id === id);
        if (s) Object.assign(s, { version: s.version + 1, updated_at: stamp() });
      });
      return json(chart(principal));
    },
  };
}
